import React, { useState, useEffect } from "react";
import { Search, Plus, Phone, Mail, Calendar, IndianRupee, Star, Edit, Trash2, CreditCard, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AddVendorDialog from "@/components/AddVendorDialog";
import EditVendorDialog from "@/components/EditVendorDialog";
import { VendorPhoto } from "@/components/VendorPhoto";
import { vendorService } from "@/services/vendor.service";
import { emailService } from "@/services/email.service";
import { openWhatsApp } from "@/utils/whatsapp";
import { useCurrency } from "@/hooks/useCurrency";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { permissions } from "@/lib/mockAuth";
import { canAddResource, isTrialExpired } from "@/utils/trialUtils";
import { BlockedActionDialog } from "@/components/BlockedActionDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const VendorModule = ({ weddingId, wedding, onStatsChange, onWeddingUpdated, refreshTrigger }) => {
  const { userRole, activeOrg } = useAuth();
  const { formatCurrency } = useCurrency();
  const canManage = permissions.canManageVendors(userRole); // Admin and Planner can manage
  
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [deletingVendor, setDeletingVendor] = useState(null);
  
  // Schedule Dialog
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, vendor: null });
  const [scheduleNotes, setScheduleNotes] = useState("");
  const [scheduleMethod, setScheduleMethod] = useState("email"); // "email" | "whatsapp"
  const [emailConfirmDialog, setEmailConfirmDialog] = useState(false);
  const [emailConfirmVendor, setEmailConfirmVendor] = useState(null); // Store vendor for email confirmation
  
  // Payment Dialog
  const [paymentDialog, setPaymentDialog] = useState({ open: false, vendor: null });
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);

  // Check trial limits - recalculate when vendors or wedding changes
  const vendorLimitCheck = React.useMemo(() => {
    if (!wedding) return { canAdd: true, message: '', limit: null };
    return canAddResource(wedding, 'vendors', vendors.length);
  }, [wedding, vendors.length]);
  
  const isBlocked = React.useMemo(() => {
    if (!wedding) return false;
    return !vendorLimitCheck.canAdd || isTrialExpired(wedding);
  }, [wedding, vendorLimitCheck]);

  // Load vendors from backend
  useEffect(() => {
    if (weddingId) {
      loadVendors();
    }
  }, [weddingId, refreshTrigger]);

  const loadVendors = async () => {
    if (!weddingId) return;
    
    try {
      setLoading(true);
      const data = await vendorService.getVendors(weddingId, activeOrg.id);
      setVendors(data);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = async (newVendorData) => {
    if (!activeOrg) {
      toast.error('Organization not found');
      return;
    }
    
    // Double-check trial limits before submitting
    const currentCheck = canAddResource(wedding, 'vendors', vendors.length);
    if (!currentCheck.canAdd || isTrialExpired(wedding)) {
      setBlockedDialogOpen(true);
      return;
    }
    
    try {
      await vendorService.createVendor(weddingId, activeOrg.id, newVendorData);
      await loadVendors(); // Reload to get complete data
      toast.success('Vendor added successfully');
    } catch (error) {
      console.error('Failed to add vendor:', error);
      // If backend returns 403, show blocked dialog
      if (error.response?.status === 403) {
        setBlockedDialogOpen(true);
      } else {
        toast.error('Failed to add vendor');
      }
    }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setIsEditDialogOpen(true);
  };

  const handleUpdateVendor = async (updatedVendorData) => {
    try {
      await vendorService.updateVendor(updatedVendorData.id, updatedVendorData);
      await loadVendors();
      toast.success('Vendor updated successfully');
    } catch (error) {
      console.error('Failed to update vendor:', error);
      toast.error('Failed to update vendor');
    }
  };

  const handleDeleteVendor = async () => {
    if (deletingVendor) {
      const vendor = deletingVendor;
      const hasPayments = vendor.advance_paid && vendor.advance_paid > 0;
      const isConfirmed = vendor.status === 'confirmed';
      const hasEvents = vendor.events && vendor.events.length > 0;
      
      try {
        await vendorService.deleteVendor(vendor.id);
        await loadVendors();
        
        // Show appropriate message based on what was deleted
        if (hasPayments || isConfirmed || hasEvents) {
          const warnings = [];
          if (hasPayments) warnings.push(`₹${vendor.advance_paid} in payments`);
          if (isConfirmed) warnings.push('confirmed status');
          if (hasEvents) warnings.push(`${vendor.events.length} event(s)`);
          
          toast.warning(
            `Vendor "${vendor.name}" deleted. Removed: ${warnings.join(', ')}.`,
            { duration: 5000 }
          );
        } else {
          toast.success(`Vendor ${vendor.name} deleted successfully`);
        }
        
        setDeletingVendor(null);
        
        // Refresh budget if callback provided
        if (onWeddingUpdated) {
          try {
            await onWeddingUpdated();
          } catch (err) {
            console.error('Failed to refresh wedding after vendor deletion:', err);
          }
        }
      } catch (error) {
        console.error('Failed to delete vendor:', error);
        toast.error('Failed to delete vendor');
      }
    }
  };

  const handleSchedule = (vendor) => {
    setScheduleDialog({ open: true, vendor });
    setScheduleNotes("");
    setScheduleMethod("email");
  };

  const handleSendEmail = async () => {
    const vendor = emailConfirmVendor || scheduleDialog.vendor;
    if (!vendor) {
      setEmailConfirmDialog(false);
      setEmailConfirmVendor(null);
      return;
    }
    
    try {
      // Send schedule via email using existing backend integration
      const result = await emailService.sendVendorSchedule(
        weddingId,
        [vendor.id]
      );
      
      if (result.sent_count > 0) {
        toast.success(
          `Schedule sent to ${vendor.name} via Email!`,
          {
            description: 'Vendor will receive the complete event timeline'
          }
        );
        // Close both dialogs after successful send
        setEmailConfirmDialog(false);
        setEmailConfirmVendor(null);
        setScheduleDialog({ open: false, vendor: null });
        setScheduleNotes("");
      } else {
        toast.error('Failed to send schedule. Please check vendor email address.');
        setEmailConfirmDialog(false);
        setEmailConfirmVendor(null);
        // Reopen schedule dialog if email failed
        setTimeout(() => {
          setScheduleDialog({ open: true, vendor: vendor });
        }, 100);
      }
    } catch (error) {
      console.error('Error sending schedule:', error);
      toast.error('Failed to send schedule. Please try again.');
      setEmailConfirmDialog(false);
      setEmailConfirmVendor(null);
      // Reopen schedule dialog if error
      setTimeout(() => {
        setScheduleDialog({ open: true, vendor: vendor });
      }, 100);
    }
  };

  const handleOpenWhatsApp = () => {
    if (!scheduleDialog.vendor) return;
    
    const vendor = scheduleDialog.vendor;
    
    if (!vendor.phone) {
      toast.error('No phone number available for this vendor');
      return;
    }

    const baseMessage = scheduleNotes?.trim() ||
      `Hi ${vendor.name}, sharing the schedule for our wedding. Please confirm once you receive it.`;

    const success = openWhatsApp(vendor.phone, baseMessage);
    
    if (success) {
      toast.success(`Opening WhatsApp for ${vendor.name}`);
      // Close dialog after opening WhatsApp
      setScheduleDialog({ open: false, vendor: null });
      setScheduleNotes("");
    } else {
      toast.error('Invalid phone number or unable to open WhatsApp');
    }
  };

  const handlePayment = (vendor) => {
    setPaymentDialog({ open: true, vendor });
    setPaymentAmount("");
    setPaymentNotes("");
  };

  const handlePaymentSubmit = async () => {
    if (paymentDialog.vendor && paymentAmount) {
      try {
        const newAdvancePaid = (paymentDialog.vendor.advance_paid || 0) + parseFloat(paymentAmount);
        
        // Update vendor in backend
        await vendorService.updateVendor(paymentDialog.vendor.id, {
          advance_paid: newAdvancePaid
        });
        
        toast.success(`Payment of ₹${paymentAmount} recorded for ${paymentDialog.vendor.name}`);
        
        // Close dialog
        setPaymentDialog({ open: false, vendor: null });
        setPaymentAmount("");
        setPaymentNotes("");
        
        // Reload vendors from API to get fresh data
        await loadVendors();

        // Ask parent to refresh wedding stats (e.g., total spent) if callback provided
        if (onWeddingUpdated) {
          try {
            await onWeddingUpdated();
          } catch (err) {
            console.error('Failed to refresh wedding after payment update:', err);
          }
        }
      } catch (error) {
        console.error('Error recording payment:', error);
        toast.error('Failed to record payment. Please try again.');
      }
    }
  };

  // Calculate stats - Include all vendors including hotels
  const totalVendors = vendors.length; // Include all vendors
  const nonHotelVendors = vendors.filter(v => v.category !== "Hotels").length;
  const confirmedVendors = vendors.filter(v => v.status === "confirmed").length;
  const negotiatingVendors = vendors.filter(v => v.status === "negotiating").length;
  const readyVendors = vendors.filter(v => v.status === "ready").length;
  const totalPaid = vendors.reduce((sum, v) => sum + (v.advance_paid || 0), 0);

  // Notify parent about vendor stats for top-level widgets
  useEffect(() => {
    if (onStatsChange) {
      onStatsChange({
        total: totalVendors,
        nonHotel: nonHotelVendors,
        totalPaid,
      });
    }
  }, [totalVendors, nonHotelVendors, totalPaid, onStatsChange, vendors]);

  // Filter vendors
  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-success text-primary-foreground";
      case "negotiating": return "bg-warning text-foreground";
      case "ready": return "bg-primary text-primary-foreground";
      case "completed": return "bg-muted text-muted-foreground";
      default: return "bg-secondary text-secondary-foreground";
    }
  };

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-t-4 border-t-primary">
          <p className="text-sm text-muted-foreground mb-1">Total Vendors</p>
          <p className="text-2xl font-bold text-foreground">{totalVendors}</p>
        </Card>
        <Card className="p-4 border-t-4 border-t-success">
          <p className="text-sm text-muted-foreground mb-1">Confirmed</p>
          <p className="text-2xl font-bold text-success">{confirmedVendors}</p>
        </Card>
        <Card className="p-4 border-t-4 border-t-warning">
          <p className="text-sm text-muted-foreground mb-1">Negotiating</p>
          <p className="text-2xl font-bold text-warning">{negotiatingVendors}</p>
        </Card>
        <Card className="p-4 border-t-4 border-t-primary">
          <p className="text-sm text-muted-foreground mb-1">Ready</p>
          <p className="text-2xl font-bold text-primary">{readyVendors}</p>
        </Card>
      </div>

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        {canManage && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    onClick={() => setIsAddDialogOpen(true)}
                    className="gradient-primary flex items-center gap-2"
                    disabled={isBlocked}
                  >
                    <Plus className="h-4 w-4" />
                    Add Vendor
                  </Button>
                </span>
              </TooltipTrigger>
              {isBlocked && (
                <TooltipContent>
                  <p>🔒 Trial limit reached<br />Upgrade for more vendors</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Vendors List */}
      <div className="space-y-4">
        {loading ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">Loading vendors...</p>
          </Card>
        ) : filteredVendors.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground text-lg mb-4">No vendors found</p>
            {canManage && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button 
                        onClick={() => setIsAddDialogOpen(true)}
                        className="gradient-primary"
                        disabled={isBlocked}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Your First Vendor
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isBlocked && (
                    <TooltipContent>
                      <p>🔒 Trial limit reached<br />Upgrade for more vendors</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </Card>
        ) : (
          filteredVendors.map((vendor) => (
            <Card key={vendor.id} className="p-4 sm:p-6 hover:shadow-md transition-shadow">
              <div className="flex gap-4">
                {/* Vendor Image */}
                {(vendor.location?.photo_url || vendor.location?.photo_reference) && (
                  <VendorPhoto
                    photoUrl={vendor.location?.photo_url}
                    photoReference={vendor.location?.photo_reference}
                    alt={vendor.name}
                    vendorName={vendor.name}
                  />
                )}
                
                {/* Vendor Details */}
                <div className="flex-1 space-y-3 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground truncate">{vendor.name}</h3>
                      <div className="mt-1">
                        <Badge variant="outline" className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border-primary/20">
                          {vendor.category}
                        </Badge>
                      </div>
                      {vendor.location?.address && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">{vendor.location.address}</p>
                      )}
                    </div>
                    
                    {/* Inline Status Editor */}
                    {canManage ? (
                      <Select 
                        value={vendor.status} 
                        onValueChange={async (newStatus) => {
                          // Validate: If changing to "confirmed", ensure events exist
                          if (newStatus === "confirmed" && (!vendor.events || vendor.events.length === 0)) {
                            toast.error('Please add at least one event before confirming this vendor. Use Edit to add events.');
                            return;
                          }
                          
                          try {
                            await vendorService.updateVendor(vendor.id, { status: newStatus });
                            setVendors(vendors.map(v => v.id === vendor.id ? { ...v, status: newStatus } : v));
                            toast.success(`Status updated to ${newStatus}`);
                          } catch (error) {
                            console.error('Failed to update status:', error);
                            toast.error('Failed to update status');
                          }
                        }}
                      >
                        <SelectTrigger className={`h-8 border-none shadow-none px-2 text-xs sm:text-sm w-[120px] sm:w-[140px] ${getStatusColor(vendor.status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="negotiating">Negotiating</SelectItem>
                          <SelectItem value="ready">Ready</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={getStatusColor(vendor.status)}>
                        {vendor.status}
                      </Badge>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {vendor.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>{vendor.phone}</span>
                      </div>
                    )}
                    {vendor.email && (
                      <div className="flex items-center gap-2 min-w-0 max-w-full">
                        <Mail className="h-4 w-4" />
                        <span className="truncate max-w-[160px] sm:max-w-xs">{vendor.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Events */}
                  <div className="flex flex-wrap gap-2">
                    {vendor.events?.map((event) => (
                      <Badge key={event} variant="outline" className="text-xs">
                        {event}
                      </Badge>
                    ))}
                  </div>

                  {/* Pricing */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 border-t">
                    <div>
                      <p className="text-xs text-muted-foreground">Quoted</p>
                      <p className="text-sm font-medium">{formatCurrency(vendor.quoted)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Negotiated</p>
                      <p className="text-sm font-medium text-success">{formatCurrency(vendor.negotiated)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Paid</p>
                      <p className="text-sm font-medium text-primary">{formatCurrency(vendor.advance_paid || 0)}</p>
                    </div>
                  </div>

                  {/* Payment Progress */}
                  {vendor.negotiated > 0 && (
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Payment Progress</span>
                        <span>{Math.round(((vendor.advance_paid || 0) / vendor.negotiated) * 100)}%</span>
                      </div>
                      <Progress value={((vendor.advance_paid || 0) / vendor.negotiated) * 100} className="h-2" />
                    </div>
                  )}

                  {/* Action Buttons - Single Row on Mobile */}
                  <div className="grid grid-cols-2 sm:flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs px-2 flex items-center justify-center gap-1"
                      onClick={() => handleSchedule(vendor)}
                    >
                      <Clock className="h-3 w-3" />
                      <span className="hidden sm:inline">Schedule</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs px-2 flex items-center justify-center gap-1"
                      onClick={() => handlePayment(vendor)}
                    >
                      <CreditCard className="h-3 w-3" />
                      <span className="hidden sm:inline">Payment</span>
                    </Button>
                    {canManage && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-2 flex items-center justify-center gap-1"
                          onClick={() => handleEditVendor(vendor)}
                        >
                          <Edit className="h-3 w-3" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-2 text-destructive hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center gap-1"
                          onClick={() => setDeletingVendor(vendor)}
                        >
                          <Trash2 className="h-3 w-3" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Add Vendor Dialog */}
      <AddVendorDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAddVendor={handleAddVendor}
        weddingId={weddingId}
      />

      {/* Edit Vendor Dialog */}
      <EditVendorDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        vendor={editingVendor}
        onUpdateVendor={handleUpdateVendor}
        weddingId={weddingId}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingVendor} onOpenChange={() => setDeletingVendor(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const vendor = deletingVendor;
                if (!vendor) return "Delete Vendor?";
                const hasPayments = vendor.advance_paid && vendor.advance_paid > 0;
                const isConfirmed = vendor.status === 'confirmed';
                const hasEvents = vendor.events && vendor.events.length > 0;
                return (hasPayments || isConfirmed || hasEvents)
                  ? "⚠️ Warning: Important Data Found"
                  : "Delete Vendor?";
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {(() => {
                const vendor = deletingVendor;
                if (!vendor) return null;
                
                const hasPayments = vendor.advance_paid && vendor.advance_paid > 0;
                const isConfirmed = vendor.status === 'confirmed';
                const hasEvents = vendor.events && vendor.events.length > 0;
                
                if (hasPayments || isConfirmed || hasEvents) {
                  const warnings = [];
                  if (hasPayments) warnings.push(`₹${formatCurrency(vendor.advance_paid)} in payments`);
                  if (isConfirmed) warnings.push('confirmed status');
                  if (hasEvents) warnings.push(`${vendor.events.length} event(s) assigned`);
                  
                  return (
                    <>
                      <p>
                        You are about to delete <strong>{vendor.name}</strong>. This vendor has important data associated:
                      </p>
                      <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
                        <p className="text-sm font-medium text-warning-foreground mb-2">
                          ⚠️ Will be removed:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                          {hasPayments && <li>All payment records (₹{formatCurrency(vendor.advance_paid)})</li>}
                          {isConfirmed && <li>Confirmed status and event assignments</li>}
                          {hasEvents && <li>Event assignments: {vendor.events.join(', ')}</li>}
                          <li>Budget category will be deleted</li>
                          <li>All vendor information and history</li>
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground pt-2">
                        This action cannot be undone. Are you sure you want to proceed?
                      </p>
                    </>
                  );
                } else {
                  return (
                    <p>
                      Are you sure you want to delete <strong>{vendor.name}</strong>? This action cannot be undone.
                    </p>
                  );
                }
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVendor}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(() => {
                const vendor = deletingVendor;
                if (!vendor) return "Delete";
                const hasPayments = vendor.advance_paid && vendor.advance_paid > 0;
                const isConfirmed = vendor.status === 'confirmed';
                const hasEvents = vendor.events && vendor.events.length > 0;
                return (hasPayments || isConfirmed || hasEvents) ? "Delete Anyway" : "Delete";
              })()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialog.open} onOpenChange={(open) => setScheduleDialog({ open, vendor: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Schedule with {scheduleDialog.vendor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="schedule-notes">Message / Notes</Label>
              <Textarea
                id="schedule-notes"
                value={scheduleNotes}
                onChange={(e) => setScheduleNotes(e.target.value)}
                placeholder="Enter your message or meeting requirements..."
                rows={4}
                className="mt-1.5"
              />
            </div>

            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-xs font-medium mb-2">Contact Information</p>
              <div className="flex flex-col gap-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" />
                  <span>{scheduleDialog.vendor?.email || 'Email not provided'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5" />
                  <span>{scheduleDialog.vendor?.phone || 'Phone not provided'}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                {scheduleNotes.trim() 
                  ? 'Your message will be included with the schedule.'
                  : 'The complete event timeline will be sent to the vendor.'}
              </p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setScheduleDialog({ open: false, vendor: null })}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                // Store vendor and close schedule dialog first, then show confirmation
                setEmailConfirmVendor(scheduleDialog.vendor);
                setScheduleDialog({ open: false, vendor: scheduleDialog.vendor });
                // Use setTimeout to ensure schedule dialog closes before confirmation opens
                setTimeout(() => {
                  setEmailConfirmDialog(true);
                }, 100);
              }}
              className="flex-1 flex items-center justify-center gap-2"
              disabled={!scheduleDialog.vendor?.email}
            >
              <Mail className="h-4 w-4" />
              <span>Email</span>
            </Button>
            <Button 
              onClick={handleOpenWhatsApp}
              variant="outline"
              className="flex-1 flex items-center justify-center gap-2"
              disabled={!scheduleDialog.vendor?.phone}
            >
              <Phone className="h-4 w-4" />
              <span>WhatsApp</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Confirmation Dialog */}
      <AlertDialog open={emailConfirmDialog} onOpenChange={(open) => {
        setEmailConfirmDialog(open);
        // If cancelled, reopen schedule dialog with stored vendor
        if (!open && emailConfirmVendor) {
          setTimeout(() => {
            setScheduleDialog({ open: true, vendor: emailConfirmVendor });
          }, 100);
        }
        if (!open) {
          setEmailConfirmVendor(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Schedule via Email?</AlertDialogTitle>
            <AlertDialogDescription>
              Send the complete event timeline to <strong>{emailConfirmVendor?.name}</strong> at <strong>{emailConfirmVendor?.email}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendEmail} className="gradient-primary">
              Send Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => setPaymentDialog({ open, vendor: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Record Payment - {paymentDialog.vendor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="payment-amount">Payment Amount *</Label>
              <Input
                id="payment-amount"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="payment-notes">Notes (Optional)</Label>
              <Textarea
                id="payment-notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Payment reference, transaction ID, etc."
                rows={3}
                className="mt-1.5"
              />
            </div>
            {paymentDialog.vendor && (
              <div className="bg-muted/50 p-3 rounded-md text-sm">
                <div className="flex justify-between mb-1">
                  <span>Total Amount:</span>
                  <span className="font-medium">{formatCurrency(paymentDialog.vendor.negotiated)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Already Paid:</span>
                  <span className="font-medium">{formatCurrency(paymentDialog.vendor.advance_paid || 0)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 mt-1">
                  <span className="font-medium">Remaining:</span>
                  <span className="font-bold">{formatCurrency(paymentDialog.vendor.negotiated - (paymentDialog.vendor.advance_paid || 0))}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog({ open: false, vendor: null })}>
              Cancel
            </Button>
            <Button onClick={handlePaymentSubmit} className="gradient-primary" disabled={!paymentAmount}>
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlockedActionDialog
        open={blockedDialogOpen}
        onOpenChange={setBlockedDialogOpen}
        resourceType="vendors"
        limit={vendorLimitCheck.limit}
        wedding={wedding}
        onUpgradeSuccess={loadVendors}
      />
    </div>
  );
};

export default VendorModule;
